import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { GET_BOTMAKER_SETTINGS } from '../../../gql/queries/botmaker'
import { Button, Form, Modal } from 'react-bootstrap'
import { CampaignChannel, ChannelVariantInput, IMessageTemplate } from '../../../types/campaigns'
import { SUPPORTED_CHANNELS } from '../../../constants/campaigns'
import { TemplateStarter } from '../../../constants/templateStarters'
import { useIntl, FormattedMessage } from 'react-intl'
import { CAMPAIGN_MESSAGES as M } from '../campaignMessages'

type TemplateFormValues = {
  name: string
  description?: string
  channel: CampaignChannel
  subject?: string
  body: string
  variables?: string[]
  metadata?: Record<string, any>
  namespace?: string
  enabledChannels?: CampaignChannel[]
  organizationId?: string
  variants?: ChannelVariantInput[]
}

type OrgOption = { id: string; name: string }

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (values: TemplateFormValues) => Promise<void>
  template?: IMessageTemplate | null
  starter?: TemplateStarter | null
  loading?: boolean
  defaultChannel?: CampaignChannel
  isDuplicate?: boolean
  organizations?: OrgOption[]
  defaultOrganizationId?: string
}



const channelOptions = SUPPORTED_CHANNELS

const parseTemplateMetadata = (metadata: unknown): Record<string, any> => {
  if (!metadata) return {}
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata)
    } catch (e) {
      console.error('Failed to parse metadata JSON', e)
      return {}
    }
  }
  if (typeof metadata === 'object') {
    return metadata as Record<string, any>
  }
  return {}
}

export const TemplateFormModal = ({ show, onHide, onSubmit, template, starter, loading, defaultChannel, isDuplicate, organizations, defaultOrganizationId }: Props) => {
  const intl = useIntl()
  const [name, setName] = useState('')
  const [organizationId, setOrganizationId] = useState(defaultOrganizationId || '')
  const [description, setDescription] = useState('')
  const [channel, setChannel] = useState<CampaignChannel>('EMAIL')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [variables, setVariables] = useState('')
  const [metadataJson, setMetadataJson] = useState('{}')
  const [namespace, setNamespace] = useState('')

  const { data: settingsData } = useQuery(GET_BOTMAKER_SETTINGS, {
    skip: channel !== 'WHATSAPP',
  })

  // Determine initial enabled channels from template
  const [enabledChannels, setEnabledChannels] = useState<CampaignChannel[]>([])

  useEffect(() => {
    if (settingsData?.botmakerSettings?.namespace && !template) {
      setNamespace(settingsData.botmakerSettings.namespace)
    }
  }, [settingsData, template])

  useEffect(() => {
    setOrganizationId(defaultOrganizationId || '')
  }, [defaultOrganizationId])

  useEffect(() => {
    if (!template) {
      const initialChannel = starter?.channel || defaultChannel || 'EMAIL'
      setName('')
      setDescription('')
      setChannel(initialChannel)
      setSubject('')
      setBody('')
      setVariables('')
      setMetadataJson('{}')
      setNamespace('')
      setEnabledChannels([initialChannel])
      return
    }
    setName(template.name)
    setDescription(template.description ?? '')
    setChannel(template.channel)
    setSubject(template.subject ?? '')
    setBody(template.body)
    setVariables(
      Array.isArray(template.variables)
        ? template.variables.join(', ')
        : (template.variables || '')
    )
    const meta = parseTemplateMetadata(template.metadata)

    setMetadataJson(JSON.stringify(meta ?? {}, null, 2))
    setNamespace(meta?.namespace || '')

    // Check metadata for enabled channels, fallback to legacy channel or just EMAIL
    const metaChannels = meta?.enabled_channels
    if (Array.isArray(metaChannels)) {
      setEnabledChannels(metaChannels)
    } else {
      setEnabledChannels(template.channel ? [template.channel] : ['EMAIL'])
    }
  }, [template, defaultChannel])

  // Pre-fill form with starter content
  useEffect(() => {
    if (starter && !template) {
      setName(starter.name)
      setDescription(starter.description)
      setChannel(starter.channel)
      setSubject(starter.content.subject ?? '')
      setBody(starter.content.bodyText)
      setVariables(starter.content.variables.join(', '))
      setEnabledChannels([starter.channel])
    }
  }, [starter, template])

  const handleChannelToggle = (ch: CampaignChannel) => {
    setEnabledChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    )
  }

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const trimmedDescription = description.trim()
    const trimmedSubject = subject.trim()
    const parsedVariables = variables
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)

    const currentMetadata = parseTemplateMetadata(metadataJson)
    const nextMetadata: Record<string, any> = {
      ...currentMetadata,
      enabled_channels: enabledChannels,
    }

    if (namespace.trim()) {
      nextMetadata.namespace = namespace.trim()
    } else {
      delete nextMetadata.namespace
    }

    if (starter && !template) {
      nextMetadata.starter_id = starter.id
    }

    // Seed one variant per enabled channel with the starter content, so the
    // variant editor opens with the chosen design instead of a blank page.
    // The HTML layout only makes sense for email; other channels get the plain text.
    const defaultVariantChannel = enabledChannels.includes(starter?.channel as CampaignChannel)
      ? starter?.channel
      : enabledChannels[0]

    const starterVariants: ChannelVariantInput[] | undefined =
      starter && !template
        ? enabledChannels.map((ch) => ({
            channel: ch,
            subject: ch === 'EMAIL' ? trimmedSubject || starter.content.subject || '' : '',
            bodyText: starter.content.bodyText,
            bodyHtml: ch === 'EMAIL' ? starter.content.bodyHtml || '' : '',
            variables: parsedVariables,
            isDefault: ch === defaultVariantChannel,
          }))
        : undefined

    const payload: TemplateFormValues = {
      name: trimmedName,
      description: trimmedDescription || undefined,
      channel: enabledChannels[0] || channel,
      subject: trimmedSubject || undefined,
      body,
      variables: parsedVariables,
      enabledChannels,
      metadata: nextMetadata,
      organizationId: organizationId || undefined,
      variants: starterVariants,
    }

    await onSubmit(payload)
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(
            isDuplicate ? M.templateFormDuplicate : template ? M.templateFormEdit : M.templateFormCreate
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isDuplicate && organizations && organizations.length > 0 && (
          <Form.Group className='mb-4'>
            <Form.Label><FormattedMessage {...M.organization} /></Form.Label>
            <Form.Select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
              <option value=''>{intl.formatMessage(M.templateFormSelectOrg)}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        <Form.Group className='mb-4'>
          <Form.Label><FormattedMessage {...M.name} /></Form.Label>
          <Form.Control
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={intl.formatMessage(M.templateFormNamePlaceholder)}
            autoFocus
          />
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label><FormattedMessage {...M.templateFormEnabledChannels} /></Form.Label>
          <div className='d-flex gap-3 flex-wrap'>
            {channelOptions.map(opt => (
              <Form.Check
                key={opt}
                type="checkbox"
                id={`channel-${opt}`}
                label={opt}
                checked={enabledChannels.includes(opt)}
                onChange={() => handleChannelToggle(opt)}
              />
            ))}
          </div>
          <Form.Text muted><FormattedMessage {...M.templateFormChannelsHint} /></Form.Text>
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label><FormattedMessage {...M.subject} /></Form.Label>
          <Form.Control
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={intl.formatMessage(M.templateFormSubjectPlaceholder)}
          />
          <Form.Text muted>
            <FormattedMessage {...M.templateFormSubjectHint} />
          </Form.Text>
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label><FormattedMessage {...M.description} /></Form.Label>
          <Form.Control
            as='textarea'
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide} disabled={loading}>
          <FormattedMessage {...M.cancel} />
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={loading || !name.trim() || enabledChannels.length === 0}
        >
          {intl.formatMessage(
            isDuplicate ? M.templatesDuplicate : template ? M.save : M.templateFormCreateAction
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
